package fr.frux.shared.data.local

import fr.frux.core.database.FruxDatabase
import fr.frux.core.maps.Location
import fr.frux.shared.domain.model.HarvestPart
import fr.frux.shared.domain.model.PlantForm
import fr.frux.shared.domain.model.Tree

/**
 * Cache local des arbres sur SQLDelight (#60) : requête par boîte englobante dérivée du rayon,
 * affinée par distance haversine, filtres forme/partie appliqués en mémoire. L'horloge est
 * injectée ([now], epoch millis) pour horodater `cached_at` de façon testable (#58). [policy]
 * pilote la fraîcheur, l'expiration et le plafond de volume (#62).
 */
class SqlDelightTreeLocalDataSource(
    private val database: FruxDatabase,
    private val policy: TreeCachePolicy,
    private val now: () -> Long,
) : TreeLocalDataSource {
    override suspend fun nearbyTrees(
        lat: Double,
        lon: Double,
        radiusKm: Double,
        form: PlantForm?,
        part: HarvestPart?,
    ): CachedTrees {
        val box = CacheBounds.around(lat, lon, radiusKm)
        val center = Location(lat, lon)
        val radiusMeters = radiusKm * 1_000
        val minCachedAt = now() - policy.evictAfterMillis
        val rows =
            database.treeQueries
                .selectInBounds(
                    latMin = box.latMin,
                    latMax = box.latMax,
                    lngMin = box.lngMin,
                    lngMax = box.lngMax,
                    minCachedAt = minCachedAt,
                ).executeAsList()
        val matched =
            rows
                .mapNotNull { row ->
                    val tree = row.toDomain() ?: return@mapNotNull null
                    val distanceMeters = Location(tree.latitude, tree.longitude).distanceMetersTo(center)
                    val matchesFilter =
                        (form == null || tree.species.form == form) &&
                            (part == null || tree.harvestPart == part)
                    if (distanceMeters <= radiusMeters && matchesFilter) {
                        Triple(tree, row.cached_at, distanceMeters)
                    } else {
                        null
                    }
                }
                // Meme contrat que le serveur (#270) : du plus proche au plus lointain, pour que
                // la troncature a `limit` du repository garde toujours le centre du viewport.
                .sortedWith(compareBy({ it.third }, { it.first.id }))
                .map { it.first to it.second }
        val isStale = matched.isNotEmpty() && matched.minOf { it.second } < now() - policy.staleAfterMillis
        return CachedTrees(trees = matched.map { it.first }, isStale = isStale)
    }

    override suspend fun upsertAll(trees: List<Tree>) {
        if (trees.isEmpty()) return
        val cachedAt = now()
        database.treeQueries.transaction {
            trees.forEach { tree ->
                val row = tree.toCacheRow(cachedAt)
                database.treeQueries.upsert(
                    id = row.id,
                    species_id = row.species_id,
                    species_common_name_fr = row.species_common_name_fr,
                    species_scientific_name = row.species_scientific_name,
                    species_form = row.species_form,
                    harvest_part = row.harvest_part,
                    latitude = row.latitude,
                    longitude = row.longitude,
                    status = row.status,
                    certainty = row.certainty,
                    disputed = row.disputed,
                    description = row.description,
                    photo_url = row.photo_url,
                    created_by = row.created_by,
                    created_at = row.created_at,
                    updated_at = row.updated_at,
                    last_observed_at = row.last_observed_at,
                    cached_at = row.cached_at,
                    site_id = row.site_id,
                )
            }
        }
    }

    override suspend fun prune() {
        val threshold = now() - policy.evictAfterMillis
        database.treeQueries.transaction {
            database.treeQueries.deleteExpired(threshold)
            val remaining = database.treeQueries.countAll().executeAsOne()
            val overflow = remaining - policy.maxRows
            if (overflow > 0) {
                database.treeQueries.deleteOldestOverflow(overflow)
            }
        }
    }

    override suspend fun deleteByCreator(userId: Long) {
        database.treeQueries.deleteByCreator(userId)
    }
}
